<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            System Labels
          </div>
        </template>
      </EsHeader>
      <div class="icon-right" v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
        <a role="button"
          @keypress.enter.prevent="editPanel()"
          :class="{ disabled: editOtherPanels }"
          @click="editPanel()"
          v-if="!editCurrentPanel"
          :tabindex="editOtherPanels ? -1 : 0"
        >
          <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
        </a>
      </div>
    </div>
    <div class="content">
      <es-loader v-if="loading" style="margin: 3rem"/>
      <table v-else>
        <tbody>
          <tr>
            <th style="padding-top: 1rem" :class="{ 'no-border': !hiddenButtonLabels }">
              Button Labels
            </th>
            <th
              align="right"
              style="padding-top: 1rem"
              :class="{ 'no-border': !hiddenButtonLabels }">
              <a role="button" tabindex="0" @keyup.enter="toggleButtonLabels()">
                <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                  title="Collapse"
                  @click="toggleButtonLabels()"
                  v-if="!hiddenButtonLabels"
                />
                <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                  title="Expand"
                  @click="toggleButtonLabels()"
                  v-if="hiddenButtonLabels"
                />
              </a>
            </th>
          </tr>
          <tr v-if="!hiddenButtonLabels">
            <th class="rule left-padding no-border" id="escholarfield">
              eScholar Field
            </th>
            <th class="rule no-border" id="displaylabel">
              System Label
            </th>
          </tr>
          <tr v-if="!hiddenButtonLabels">
            <td class="rule left-padding" :class="{ 'no-border': editCurrentPanel }" headers="escholarfield">
              No Match
            </td>
            <td :class="{ 'no-border': editCurrentPanel }" headers="displaylabel">
              <div class="control">
                <input
                  class="input is-pulled-right"
                  :class="{ 'is-static': !editCurrentPanel }"
                  maxlength="35"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="noMatch"
                  :aria-label="noMatch || 'No Match'"
                  @keyup.prevent="validateSystemLabelField(noMatch)"
                >
                <span v-if="errNoMatch" class="help is-danger">
                  {{ msgNoMatch }}
                </span>
              </div>
            </td>
          </tr>
          <tr v-if="!hiddenButtonLabels">
            <td class="rule left-padding bottom-padding" headers="escholarfield">
              Match
            </td>
            <td class="bottom-padding" headers="displaylabel">
              <div class="control">
                <input
                  class="input is-pulled-right"
                  :class="{ 'is-static': !editCurrentPanel }"
                  maxlength="35"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="match"
                  :aria-label="match || 'Match'"
                  @keyup.prevent="validateSystemLabelField(match)"
                >
                <span v-if="errMatch" class="help is-danger">
                  {{ msgMatch }}
                </span>
              </div>
            </td>
          </tr>
          <tr>
            <th :class="{ 'no-border': !hiddenComponentsLabels }">
              Component Labels
            </th>
            <th align="right" :class="{ 'no-border': !hiddenComponentsLabels }">
              <a role="button" tabindex="0" @keyup.enter="toggleComponentsLabels()">
                <font-awesome-icon :icon="['far', 'caret-up']" size="lg"
                  title="Collapse"
                  @click="toggleComponentsLabels()"
                  v-if="!hiddenComponentsLabels"
                />
                <font-awesome-icon :icon="['far', 'caret-down']" size="lg"
                  title="Expand"
                  @click="toggleComponentsLabels()"
                  v-if="hiddenComponentsLabels"
                />
              </a>
            </th>
          </tr>
          <tr v-if="!hiddenComponentsLabels">
            <th class="rule left-padding no-border" id="escholarfield">
              eScholar Field
            </th>
            <th class="rule no-border" id="displaylabel">
              System Label
            </th>
          </tr>
          <tr v-if="!hiddenComponentsLabels">
            <td class="rule left-padding bottom-padding" headers="escholarfield">
              Near Match
            </td>
            <td class="bottom-padding" headers="displaylabel">
              <div class="control">
                <input
                  class="input is-pulled-right"
                  :class="{ 'is-static': !editCurrentPanel }"
                  maxlength="35"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="nearMatch"
                  :aria-label="nearMatch || 'Near Match'"
                  @keyup.prevent="validateSystemLabelField(nearMatch)"
                >
                <span v-if="errNearMatch" class="help is-danger">
                  {{ msgNearMatch }}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 2rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretUp,
  faCaretDown,
} from '@fortawesome/pro-regular-svg-icons';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faCaretUp,
  faCaretDown,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsHeader,
    EsLoader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return {
      editCurrentPanel: false,
      hiddenButtonLabels: false,
      hiddenComponentsLabels: false,
      match: 'Match',
      nearMatch: 'Near Match',
      noMatch: 'No Match',
      loading: false,
      errNearMatch: false,
      msgNearMatch: null,
      errNoMatch: false,
      msgNoMatch: null,
      errMatch: false,
      msgMatch: null,
    };
  },
  created() {
    this.initialize(this.properties);
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    initialize(data) {
      this.nearMatch = data['SYSTEM_LABEL-NEARMATCH'];
      this.match = data['BUTTON_LABEL-ASSIGN_EXISTING'];
      this.noMatch = data['BUTTON_LABEL-CREATE_NEW'];
    },
    cancelPanel() {
      this.editCurrentPanel = false;
      this.errNearMatch = false;
      this.errNoMatch = false;
      this.errMatch = false;
      this.initialize(this.properties);
      this.$emit('allowEditOtherPanels');
    },
    toggleButtonLabels() {
      this.hiddenButtonLabels = !this.hiddenButtonLabels;
    },
    toggleComponentsLabels() {
      this.hiddenComponentsLabels = !this.hiddenComponentsLabels;
    },
    editPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.$emit('allowEditOtherPanels');
    },
    async updatePanel() {
      try {
        this.loading = true;
        this.hideNotification();
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        await this.initProperties();
        this.showNotification('success', ['The System Labels have been updated successfully.']);
        this.editCurrentPanel = false;
        this.$emit('allowEditOtherPanels');
      } catch (error) {
        const apiMsg = 'There was an issue. Please try again or contact your administrator.';
        const dataerrors = error.response.data.map(o => o.error);
        this.showNotification('danger', dataerrors || [apiMsg]);
      } finally {
        this.loading = false;
      }
    },
    validateSystemLabelField(userInput) {
      if (userInput === this.nearMatch) {
        if (!this.nearMatch.length) {
          this.errNearMatch = true;
          this.msgNearMatch = 'Component label for Near Match is required';
        } else {
          this.errNearMatch = false;
          this.msgNearMatch = '';
        }
      }
      if (userInput === this.noMatch) {
        if (!this.noMatch.length) {
          this.errNoMatch = true;
          this.msgNoMatch = 'Button label for No Match is required';
        } else {
          this.errNoMatch = false;
          this.msgNoMatch = '';
        }
      }
      if (userInput === this.match) {
        if (!this.match.length) {
          this.errMatch = true;
          this.msgMatch = 'Button label for Match is required';
        } else {
          this.errMatch = false;
          this.msgMatch = '';
        }
      }
    },
    prepForRequest() {
      const payload = [];
      payload.push({ category: 'SYSTEM_LABEL', property: 'NEARMATCH', value: this.nearMatch });
      payload.push({ category: 'BUTTON_LABEL', property: 'ASSIGN_EXISTING', value: this.match });
      payload.push({ category: 'BUTTON_LABEL', property: 'CREATE_NEW', value: this.noMatch });
      return payload;
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
  table td
    vertical-align: middle
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .header
    display: flex
    align-items: center
  .icon-right
    padding-right: 1rem
  .no-border
    border: 0
  .rule
    font-size: 12px
    color: #666
  .left-padding
    padding-left: 2.5rem
  .bottom-padding
    padding-bottom: 1rem
</style>
